import { useEffect, useState } from "react";

/**
 * Returns a copy of `value` that only updates after `delayMs` have elapsed with
 * no further change. Use it to throttle rapidly-changing inputs (e.g. text
 * typed into a filter box) so that values derived from it — like a React Query
 * key — settle once the user pauses instead of firing on every keystroke.
 *
 * The input stays bound to the immediate state so typing feels instant; only
 * the debounced copy lags, so wire the debounced copy into the query, not the UI.
 */
export function useDebouncedValue<T>(value: T, delayMs = 400): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}
